import Icon from 'app/components/partials/icon';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import React from 'react';

/*
    A carousel arrow that also carries the listing's next or previous page.

    Whenever such a page exists the arrow is a real anchor pointing at it, even
    while the carousel still has slides to scroll through. That is deliberate: the
    href is the only trail a crawler has from one page of the listing to the next,
    and it would be useless if it only appeared after someone scrolled to the end
    of a carousel. It also means the arrow still works with JavaScript off.

    A click follows that href only once the carousel is out of slides, so for a
    reader it behaves as an ordinary arrow until there is nothing left to show,
    and then quietly loads the next page in. Whether there is anything left is
    react-slick's call rather than ours, as it clones the arrow with `onClick` set
    to null at the end, which also keeps this right across the breakpoints where
    the number of slides in view changes.
*/
const CarouselPageArrow = ({ direction, label, modifier, onClick, to }) => {
    const className = `change-slide ${direction}${modifier ? ` ${modifier}` : ''}`;
    const icon = <Icon name="chevron" title={label} />;

    if (to) {
        const scrollInstead = onClick && ((event) => {
            // react-router leaves the navigation alone once the default is
            // stopped, so the carousel moves and the URL stays where it is
            event.preventDefault();
            onClick(event);
        });

        return (
            <Link className={className} aria-label={label} to={to} onClick={scrollInstead || undefined}>
                {icon}
            </Link>
        );
    }

    if (onClick) {
        return (
            <button className={className} aria-label={label} onClick={onClick}>
                {icon}
            </button>
        );
    }

    return (
        <button className={`${className} is-disabled`} aria-label={label} disabled>
            {icon}
        </button>
    );
};

CarouselPageArrow.propTypes = {
    direction: PropTypes.oneOf(['previous', 'next']).isRequired,
    label: PropTypes.string.isRequired,
    // Named where the arrow is used outside a carousel, the styling for it
    // living with whatever it has been put in rather than with the carousels
    modifier: PropTypes.string,
    // Passed in by react-slick, null once the carousel is at that end
    onClick: PropTypes.func,
    // Where to go when the carousel is at that end, null when there is no page
    to: PropTypes.object
};

export default CarouselPageArrow;
